import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';
import { canCopyImage, canSaveAs, canShareFiles, copyImage, saveImage, saveImageAs, shareImage } from '../lib/imageActions';

/*
 * Full-screen photo viewer, as in WhatsApp: tapping a photo in a chat opens it large instead of downloading it.
 * Double-click (double-tap) or the wheel zooms, dragging moves a zoomed photo. Esc, a tap beside the photo or
 * swiping it down closes. An album (several photos sent together) goes left and right: arrows, the keys, or
 * swiping sideways. Saving is the download button, or the browser's own "Save image" on right-click / long-press.
 * A single tap on the photo hides the bars so only the photo shows; another tap brings them back.
 */

interface Shown {
  src: string;
  name: string;
  /** What "download" saves (the full file; `src` may be a lighter version). */
  downloadSrc?: string;
}

let current: { list: Shown[]; index: number } | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Opens a photo; with `album`, the photos sent with it, to go through (the photo must be among them). */
export function openImage(image: Shown, album?: Shown[]): void {
  const list = album && album.length > 1 ? album : [image];
  current = { list, index: Math.max(0, list.findIndex((s) => s.src === image.src)) };
  emit();
}
function closeImage() {
  current = null;
  emit();
}
function step(by: number) {
  if (!current) return;
  const index = current.index + by;
  if (index < 0 || index >= current.list.length) return;
  current = { ...current, index };
  emit();
}

const MIN = 1;
const MAX = 5;

export function ImageViewerHost() {
  const shown = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
  const image = shown?.list[shown.index];
  return shown && image ? createPortal(<Album image={image} index={shown.index} count={shown.list.length} />, document.body) : null;
}

/** Stays while the viewer is open, so the bars stay hidden (or shown) going through an album. */
function Album({ image, index, count }: { image: Shown; index: number; count: number }) {
  const immersive = useImmersive();
  return <Viewer key={image.src} image={image} index={index} count={count} immersive={immersive} />;
}

/** How long a tap waits for a second one: a double tap zooms and must not also hide the bars. */
const TAP_DELAY = 300;

export interface Immersive {
  /** Only the picture shows: the bars are hidden. */
  on: boolean;
  /** A tap on the picture: toggles, once it is clear it was not the first half of a double tap. */
  tap: (e: React.MouseEvent) => void;
  /** A double tap (zoom) happened: the pending tap is not one. */
  cancel: () => void;
  /** Focus reached a control (keyboard, screen reader): the bars come back. */
  onFocus: (e: React.FocusEvent) => void;
}

/**
 * Immersive viewing, as in WhatsApp, Telegram and iOS Photos: one tap on the picture hides everything around
 * it (`.is-immersive` on the viewer), another brings it back. Any key but Esc and focus on a control show the
 * bars again, so keyboard users never lose them. While a viewer is open the status / title bar is black.
 */
export function useImmersive(): Immersive {
  const [on, setOn] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key !== 'Escape' && setOn(false);
    document.addEventListener('keydown', key);
    // theme-color tints the installed app's status bar (and the browser's bar): black above a black viewer.
    const metas = [...document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')];
    const before = metas.map((m) => m.content);
    for (const m of metas) m.content = '#000000';
    return () => {
      document.removeEventListener('keydown', key);
      clearTimeout(timer.current);
      metas.forEach((m, i) => (m.content = before[i]!));
    };
  }, []);
  return {
    on,
    tap: (e) => {
      clearTimeout(timer.current);
      if (e.detail > 1) return;
      timer.current = setTimeout(() => setOn((v) => !v), TAP_DELAY);
    },
    cancel: () => clearTimeout(timer.current),
    // Focus moving within the viewer (keyboard, screen reader); not the viewer focusing its close button as each
    // photo comes (focus then arrives from outside), and keys are handled above.
    onFocus: (e) => {
      if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) setOn(false);
    },
  };
}

/** Whether a point is on the photo itself (it is drawn "contain" inside a larger box: the rest is dark margin). */
function onPicture(img: HTMLImageElement, x: number, y: number): boolean {
  const box = img.getBoundingClientRect();
  if (!img.naturalWidth || !img.naturalHeight) return true;
  const scale = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  const left = box.left + (box.width - w) / 2;
  const top = box.top + (box.height - h) / 2;
  return x >= left && x <= left + w && y >= top && y <= top + h;
}

function Viewer({ image, index, count, immersive }: { image: Shown; index: number; count: number; immersive: Immersive }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null);
  /** At normal size: a swipe down closes, a swipe sideways goes to the next photo of an album. */
  const swipe = useRef<{ x: number; y: number; dir: 'v' | 'h' | null } | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const closeBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImage();
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(MAX, z * 1.25));
      if (e.key === '-') setZoom((z) => (z / 1.25 < 1.05 ? MIN : z / 1.25));
      if (e.key === '0') {
        setZoom(1);
        setPan({ x: 0, y: 0 });
      }
      // Arrow keys move a zoomed photo; at normal size, left and right go through an album.
      const move = 60;
      const zoomed = zoomRef.current > 1;
      if (e.key === 'ArrowLeft') {
        if (zoomed) setPan((p) => ({ ...p, x: p.x + move }));
        else step(-1);
      }
      if (e.key === 'ArrowRight') {
        if (zoomed) setPan((p) => ({ ...p, x: p.x - move }));
        else step(1);
      }
      if (e.key === 'ArrowUp') setPan((p) => ({ ...p, y: p.y + move }));
      if (e.key === 'ArrowDown') setPan((p) => ({ ...p, y: p.y - move }));
    };
    document.addEventListener('keydown', key);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', key);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  // Back at "fit" (keys included): the photo is centred again.
  if (zoom === 1 && (pan.x !== 0 || pan.y !== 0)) setPan({ x: 0, y: 0 });

  const setZoomTo = (z: number) => {
    // Close to 100% snaps to "fit", so zooming out always ends at the whole photo.
    const next = Math.min(MAX, Math.max(MIN, z < 1.05 ? 1 : z));
    setZoom(next);
    if (next === 1) setPan({ x: 0, y: 0 });
  };

  const full = image.downloadSrc ?? image.src;
  const [note, setNote] = useState<string | null>(null);
  const run = (fn: () => Promise<void>, done?: string) =>
    void fn().then(
      () => done && setNote(done),
      () => setNote(t('viewer.failed')),
    );
  useEffect(() => {
    if (!note) return;
    const timer = setTimeout(() => setNote(null), 2200);
    return () => clearTimeout(timer);
  }, [note]);

  return (
    <div
      className={`image-viewer${immersive.on ? ' is-immersive' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={image.name}
      style={offset.y > 0 ? { backgroundColor: `rgb(8 10 14 / ${Math.max(0.3, 0.94 - offset.y / 500)})` } : undefined}
      onClick={(e) => e.target === e.currentTarget && closeImage()}
      onFocus={immersive.onFocus}
    >
      <div className="image-viewer-bar">
        <span className="truncate">{count > 1 ? `${index + 1} / ${count} · ${image.name}` : image.name}</span>
        <span className="spacer" />
        {/* Zoom out / percentage (click: back to fit) / zoom in; the wheel, double-click and + / - work too. */}
        <button type="button" className="image-viewer-btn" disabled={zoom <= MIN} onClick={() => setZoomTo(zoom / 1.25)} aria-label={t('viewer.zoomOut')} title={t('viewer.zoomOut')}>
          <Icon name="minus" size={20} />
        </button>
        <button type="button" className="image-viewer-btn image-viewer-zoom" onClick={() => setZoomTo(1)} title={t('viewer.fit')} aria-label={t('viewer.fit')}>
          {Math.round(zoom * 100)}%
        </button>
        <button type="button" className="image-viewer-btn" disabled={zoom >= MAX} onClick={() => setZoomTo(zoom * 1.25)} aria-label={t('viewer.zoomIn')} title={t('viewer.zoomIn')}>
          <Icon name="plus" size={20} />
        </button>
        {canCopyImage() && (
          <button type="button" className="image-viewer-btn" onClick={() => run(() => copyImage(full), t('viewer.copied'))} aria-label={t('viewer.copy')} title={t('viewer.copy')}>
            <Icon name="copy" size={20} />
          </button>
        )}
        {canShareFiles() && (
          <button type="button" className="image-viewer-btn" onClick={() => run(() => shareImage(full, image.name))} aria-label={t('viewer.share')} title={t('viewer.share')}>
            <Icon name="share" size={20} />
          </button>
        )}
        {canSaveAs() && (
          <button type="button" className="image-viewer-btn" onClick={() => run(() => saveImageAs(full, image.name))} aria-label={t('viewer.saveAs')} title={t('viewer.saveAs')}>
            <Icon name="saveAs" size={20} />
          </button>
        )}
        <button type="button" className="image-viewer-btn" onClick={() => run(() => saveImage(full, image.name), t('viewer.saved'))} aria-label={t('viewer.save')} title={t('viewer.save')}>
          <Icon name="download" size={20} />
        </button>
        <button ref={closeBtn} type="button" className="image-viewer-btn" onClick={closeImage} aria-label={t('common.close')} title={t('common.close')}>
          <Icon name="close" size={22} />
        </button>
      </div>
      <div
        className={`image-viewer-stage${zoom > 1 ? ' is-zoomed' : ''}`}
        onClick={(e) => {
          if (drag.current?.moved || swipe.current?.dir) return;
          // The previous / next arrows are buttons of their own, not a tap beside the photo.
          if ((e.target as HTMLElement).closest('button')) return;
          // Beside the photo (the dark margin around it, or outside it): close. On it: only the photo shows, or
          // the bars come back; while they are hidden any tap brings them back first. A zoomed photo never
          // closes on a tap (dragging it captures the pointer, so the click lands on the stage itself).
          const img = e.currentTarget.querySelector('img');
          const beside = zoom === 1 && (e.target === e.currentTarget || (img && !onPicture(img, e.clientX, e.clientY)));
          if (beside && !immersive.on) closeImage();
          else immersive.tap(e);
        }}
        onWheel={(e) => setZoomTo(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15))}
        title={zoom > 1 ? t('viewer.dragHint') : undefined}
        onDoubleClick={() => {
          immersive.cancel();
          setZoomTo(zoom === 1 ? 2.5 : 1);
        }}
        onPointerDown={(e) => {
          if (zoom === 1) {
            if (e.isPrimary) swipe.current = { x: e.clientX, y: e.clientY, dir: null };
            return;
          }
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y, moved: false };
        }}
        onPointerMove={(e) => {
          const s = swipe.current;
          if (s) {
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            if (!s.dir && Math.hypot(dx, dy) > 10) {
              s.dir = Math.abs(dy) > Math.abs(dx) ? 'v' : 'h';
              (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            }
            if (s.dir === 'v') setOffset({ x: 0, y: Math.max(0, dy) });
            if (s.dir === 'h' && count > 1) setOffset({ x: dx, y: 0 });
            return;
          }
          const d = drag.current;
          if (!d) return;
          d.moved ||= Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 4;
          setPan({ x: d.px + (e.clientX - d.x) / zoom, y: d.py + (e.clientY - d.y) / zoom });
        }}
        onPointerUp={() => {
          const s = swipe.current;
          if (s?.dir === 'v' && offset.y > 110) closeImage();
          else if (s?.dir === 'h' && Math.abs(offset.x) > 70) step(offset.x < 0 ? 1 : -1);
          setOffset({ x: 0, y: 0 });
          // The click that follows a swipe is not a tap.
          setTimeout(() => {
            drag.current = null;
            swipe.current = null;
          }, 0);
        }}
        onPointerCancel={() => {
          swipe.current = null;
          setOffset({ x: 0, y: 0 });
        }}
      >
        <img
          src={image.src}
          alt={image.name}
          draggable={false}
          className={offset.x || offset.y ? 'is-swiping' : undefined}
          style={{ transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px) scale(${1 - Math.min(0.25, offset.y / 1200)})` : `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)` }}
        />
        {count > 1 && index > 0 && (
          <button type="button" className="image-viewer-nav is-prev" onClick={() => step(-1)} aria-label={t('viewer.previous')} title={t('viewer.previous')}>
            <Icon name="back" size={22} />
          </button>
        )}
        {count > 1 && index < count - 1 && (
          <button type="button" className="image-viewer-nav is-next" onClick={() => step(1)} aria-label={t('viewer.next')} title={t('viewer.next')}>
            <Icon name="chevronRight" size={22} />
          </button>
        )}
      </div>
      {note && (
        <p className="image-viewer-note" role="status">
          {note}
        </p>
      )}
    </div>
  );
}
